import moment from "moment-timezone";
import { useCallback, useEffect, useRef, useState } from "react";
import { getAnyaAssistant, getAnyaMessage } from "restAPI/AnyaAPI";
import useAnyaAgentSwitcher from "views/Dashboards/AnyaAgentSwitcher/useAnyaAgentSwitcher";
import { ANYA_POLLING_CONFIG } from "views/Dashboards/anyaConstants";
import {
  isResponseCompleted,
  isStatusInProgress,
} from "views/Dashboards/anyaUtils";

type AnyaAgentExecutionResponse = {
  reasoningSummary?: string;
  details?: string;
  status?: string;
  requestId?: string;
  conversationId?: string;
};

const userLocale = navigator.language || "en-US";
const userZoneId = moment.tz.guess();

export default function useFindingAnyaAgentsTab(findingId?: string | number) {
  const {
    agentsLoading,
    agentsError,
    agentOptions,
    fetchAgents,
    selectedAgent,
    setSelectedAgent,
    selectAgent,
  } = useAnyaAgentSwitcher({
    autoSelectDefaultAgent: true,
    pageSize: 5,
  });
  const [agentResponse, setAgentResponse] =
    useState<AnyaAgentExecutionResponse | null>(null);
  const [agentResponseLoading, setAgentResponseLoading] = useState(false);
  const [agentResponseError, setAgentResponseError] = useState("");
  const [agentResponseExists, setAgentResponseExists] = useState(true);
  const activeRequestCancelRef = useRef<() => void>(() => {});
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    function resetAgentStateOnFindingChange() {
      setSelectedAgent(null);
      setAgentResponse(null);
      setAgentResponseLoading(false);
      setAgentResponseError("");
      setAgentResponseExists(true);
    },
    [findingId, setSelectedAgent],
  );

  const clearActiveAgentRequest = useCallback(() => {
    activeRequestCancelRef.current();
    activeRequestCancelRef.current = () => {};

    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const fetchSelectedAgentResponse = useCallback(
    async (
      nextFindingId: string | number | undefined = findingId,
      nextSelectedAgent = selectedAgent,
    ) => {
      const pollingStartTime = Date.now();

      clearActiveAgentRequest();

      if (!nextFindingId || !nextSelectedAgent) {
        setAgentResponse(null);
        setAgentResponseLoading(false);
        setAgentResponseError("");
        setAgentResponseExists(true);
        return;
      }

      const waitForDelay = (delay: number) =>
        new Promise<void>((resolve) => {
          pollTimeoutRef.current = setTimeout(() => {
            pollTimeoutRef.current = null;
            resolve();
          }, delay);
        });

      const handleResponseComplete = (response: AnyaAgentExecutionResponse) => {
        const hasContent = response?.details;
        setAgentResponse(hasContent ? response : null);
        setAgentResponseExists(Boolean(hasContent));
        setAgentResponseLoading(false);
      };

      const pollUntilComplete = async (
        conversationId: string,
        requestId: string,
      ) => {
        if (Date.now() - pollingStartTime > ANYA_POLLING_CONFIG.MAX_POLL_TIME) {
          setAgentResponseError("Request timed out. Please try again.");
          setAgentResponseLoading(false);
          return;
        }

        const pollRequest = getAnyaMessage({
          conversationId,
          messageId: requestId,
        });
        activeRequestCancelRef.current = pollRequest.cancel;

        try {
          const result = await pollRequest.promise;
          const response = result?.data || result;

          if (isResponseCompleted(response)) {
            handleResponseComplete(response);
            return;
          }
        } catch (error: any) {
          setAgentResponseError("Failed to load agent output");
          setAgentResponseLoading(false);
          return;
        }

        await waitForDelay(ANYA_POLLING_CONFIG.SUBSEQUENT_POLL_INTERVAL);
        return await pollUntilComplete(conversationId, requestId);
      };

      setAgentResponse(null);
      setAgentResponseLoading(true);
      setAgentResponseError("");
      setAgentResponseExists(true);

      const request = getAnyaAssistant({
        query: `${nextFindingId}`,
        conversation_history: [],
        widgetTitle: "",
        widgetType: String(nextFindingId),
        dashboardId: String(nextFindingId),
        locale: userLocale,
        zoneId: userZoneId,
        agentId: nextSelectedAgent.id,
      });
      activeRequestCancelRef.current = request.cancel;

      try {
        const result = await request.promise;

        const response = result?.data;
        if (response?.status && !isStatusInProgress(response)) {
          handleResponseComplete(response);
          return;
        }

        if (response?.requestId && response?.conversationId) {
          await waitForDelay(ANYA_POLLING_CONFIG.INITIAL_POLL_DELAY);

          await pollUntilComplete(response.conversationId, response.requestId);
          return;
        }

        setAgentResponseError("Invalid response from AI service");
        setAgentResponseLoading(false);
      } catch (error: any) {
        setAgentResponseError("Failed to load agent output");
        setAgentResponseLoading(false);
      } finally {
        activeRequestCancelRef.current = () => {};

        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
      }
    },
    [clearActiveAgentRequest, findingId, selectedAgent],
  );

  useEffect(() => {
    void fetchSelectedAgentResponse();

    return () => {
      clearActiveAgentRequest();
    };
  }, [clearActiveAgentRequest, fetchSelectedAgentResponse]);

  const retryAgentResponse = useCallback(() => {
    void fetchSelectedAgentResponse();
  }, [fetchSelectedAgentResponse]);

  return {
    agentsLoading,
    agentsError,
    agentOptions,
    searchAgents: fetchAgents,
    selectedAgent,
    selectAgent,
    agentResponse,
    agentResponseLoading,
    agentResponseError,
    agentResponseExists,
    retryAgentResponse,
  };
}
